import React, { useCallback, useContext, useEffect, useState } from 'react'
import GlobalContext from '../../providers/GlobalContext'
import useUnify from '../../hooks/useUnify'
import { UNIFY_PROMO } from '../../constants/env'
import { BANNER_REFRESH_EVENT, OPEN_TOUR_EVENT, bannerAllowedAt, snoozeBanner } from './bannerSchedule'
import { BANNER } from './copy'

const BODY_CLASS = 'has-unify-banner'

const UnifyBanner = React.memo(() => {
  const { loggedInUser } = useContext(GlobalContext)
  const { switchToUnify } = useUnify()
  const [allowed, setAllowed] = useState(false)
  const [busy, setBusy] = useState(false)
  const userId = loggedInUser?.id

  useEffect(() => {
    if (!userId) return undefined
    let timer
    const evaluate = () => {
      clearTimeout(timer)
      const wait = bannerAllowedAt(userId) - Date.now()
      if (wait <= 0) {
        setAllowed(true)
        return
      }
      setAllowed(false)
      timer = setTimeout(evaluate, Math.min(wait, 60 * 60 * 1000))
    }
    evaluate()
    window.addEventListener(BANNER_REFRESH_EVENT, evaluate)
    return () => {
      clearTimeout(timer)
      window.removeEventListener(BANNER_REFRESH_EVENT, evaluate)
    }
  }, [userId])

  const visible = UNIFY_PROMO && Boolean(loggedInUser) && !loggedInUser?.unifyOptIn && allowed

  useEffect(() => {
    if (!visible) return undefined
    document.body.classList.add(BODY_CLASS)
    return () => document.body.classList.remove(BODY_CLASS)
  }, [visible])

  const dismiss = useCallback(() => {
    setAllowed(false)
    snoozeBanner(userId)
  }, [userId])

  const switchNow = useCallback(async () => {
    setBusy(true)
    const ok = await switchToUnify({ optIn: true })
    if (!ok) setBusy(false)
  }, [switchToUnify])

  const openTour = useCallback(() => window.dispatchEvent(new Event(OPEN_TOUR_EVENT)), [])

  if (!visible) return null

  return (
    <aside className='unify-promo unify-banner' aria-label='Unify announcement'>
      <div className='unify-banner__glow' aria-hidden='true' />
      <img className='unify-banner__mark' src={`${process.env.PUBLIC_URL || ''}/img/unify/mark.png`} alt='' aria-hidden='true' />
      <div className='unify-banner__text'>
        <span className='unify-label'>{BANNER.label}</span>
        <p className='unify-banner__title'>{BANNER.title}</p>
        <p className='unify-banner__lede'>{BANNER.lede}</p>
      </div>
      <div className='unify-banner__actions'>
        <button type='button' className='unify-pill unify-pill--light' disabled={busy} onClick={switchNow}>
          {BANNER.switchButton}
        </button>
        <button type='button' className='unify-pill unify-pill--line' disabled={busy} onClick={openTour}>
          {BANNER.tourButton}
        </button>
      </div>
      <button type='button' className='unify-banner__close' aria-label={BANNER.dismiss} onClick={dismiss}>
        <span aria-hidden='true'>&times;</span>
      </button>
    </aside>
  )
})

export default UnifyBanner
